import React from 'react';
import { Box, Typography, IconButton, Button, Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';

import { PageHeader, EntityDrawer, ConfirmDialog, SkeletonCards, EmptyState, ErrorState, PermissionState } from '../ui';
import CategoryFormDrawer from './CategoryFormDrawer';
import useCategoriesPage from './useCategoriesPage';
import { radius } from '../../themes/smartinventoryTheme';

// Card list, not a grid (CHANGES.md doesn't touch Categories' DataGrid because
// there never was one worth keeping — a few dozen rows read better as a
// list). States are composed directly from the same Step 3 primitives
// DataTableCard uses internally, since this page has no grid to wrap.
const Categories = () => {
  const p = useCategoriesPage();
  const showList = p.effectiveStatus === 'ready' && p.effectiveRows.length > 0;

  return (
    <Box>
      <PageHeader
        title={p.t('Categories')}
        subtitle={p.t('{{count}} categories', { count: p.total, defaultValue: `${p.total} categories` })}
        search={{ value: p.searchInput, onChange: (e) => p.setSearchInput(e.target.value), placeholder: p.t('Search Products') }}
        helpKey="categories"
        actions={[
          { label: p.t('Create Category'), icon: AddIcon, variant: 'contained', onClick: p.openCreateDrawer },
        ]}
      />

      <Box sx={{ border: showList ? '1px solid' : 'none', borderColor: 'divider', borderRadius: `${radius.xl}px`, overflow: 'hidden', bgcolor: showList ? 'background.paper' : 'transparent', maxWidth: 780 }}>
        {p.effectiveStatus === 'loading' && <SkeletonCards />}

        {p.effectiveStatus === 'error' && (
          <ErrorState
            scope="page"
            title={p.t("Couldn't load categories", { defaultValue: "Couldn't load categories" })}
            description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
            request={p.errorInfo?.request}
            retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
            onRetry={p.fetchCategories}
          />
        )}

        {p.effectiveStatus === 'permission' && (
          <PermissionState
            title={p.t("You don't have access to Categories", { defaultValue: "You don't have access to Categories" })}
            description={p.t('Ask your workspace owner for access.', { defaultValue: 'Ask your workspace owner for access.' })}
          />
        )}

        {p.effectiveStatus === 'ready' && p.effectiveRows.length === 0 && (
          p.isNoMatch ? (
            <EmptyState
              kind="no-match"
              title={p.t('No products match "{{query}}"', { query: p.search, defaultValue: `No categories match "${p.search}"` })}
              description={p.t('Try a different search.', { defaultValue: 'Try a different search.' })}
              actions={[{ label: p.t('Clear filters', { defaultValue: 'Clear filters' }), variant: 'text', onClick: () => { p.setSearchInput(''); p.setSearch(''); } }]}
            />
          ) : (
            <EmptyState
              kind="none"
              icon={CategoryIcon}
              title={p.t('No categories yet', { defaultValue: 'No categories yet' })}
              description={p.t('Create a category to start color-coding your catalog.', { defaultValue: 'Create a category to start color-coding your catalog.' })}
              actions={[{ label: p.t('Create Category'), variant: 'contained', onClick: p.openCreateDrawer }]}
            />
          )
        )}

        {showList && p.effectiveRows.map((category) => (
          <Box
            key={category._id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.75,
              px: 3,
              py: 1.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 'none' },
              '&:hover .category-row-actions': { opacity: 1 },
            }}
          >
            <Box sx={{ width: 28, height: 28, borderRadius: `${radius.sm}px`, bgcolor: category.categoryColor, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" noWrap>{category.categoryName}</Typography>
              {category.productCount != null && (
                <Typography variant="body2" color="text.secondary">
                  {p.t('{{count}} products', { count: category.productCount, defaultValue: `${category.productCount} products` })}
                </Typography>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">{category.categoryColor}</Typography>
            <Box className="category-row-actions" sx={{ display: 'flex', gap: 0.5, opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.15s' }}>
              <IconButton size="small" onClick={() => p.openEditDrawer(category)} aria-label={p.t('Edit')}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => p.openDeleteConfirm(category)} aria-label={p.t('Delete')}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>

      {p.remaining > 0 && showList && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            variant="outlined"
            onClick={p.loadMore}
            disabled={p.loadingMore}
            sx={{ borderRadius: `${radius.pill}px` }}
          >
            {p.t('Load {{count}} more · {{remaining}} remaining', {
              count: p.nextBatchSize, remaining: p.remaining, defaultValue: `Load ${p.nextBatchSize} more · ${p.remaining} remaining`,
            })}
          </Button>
        </Box>
      )}

      <EntityDrawer
        open={p.drawerOpen}
        onClose={p.closeDrawer}
        title={p.drawerMode === 'create' ? p.t('Create New Category') : p.t('Edit Category')}
        closeLabel={p.t('Close', { defaultValue: 'Close' })}
        cancelLabel={p.t('Cancel')}
        primaryLabel={p.saving
          ? (p.drawerMode === 'create' ? p.t('Creating...') : p.t('Updating...'))
          : (p.drawerMode === 'create' ? p.t('Create') : p.t('Update'))}
        onPrimary={p.handleSave}
        primaryDisabled={p.saving}
        loading={p.saving}
      >
        <CategoryFormDrawer t={p.t} form={p.form} setForm={p.setForm} formErrors={p.formErrors} saving={p.saving} />
      </EntityDrawer>

      <ConfirmDialog
        open={p.confirmOpen}
        onClose={() => p.setConfirmOpen(false)}
        tone="danger"
        icon={DeleteIcon}
        title={p.confirmTarget?.productCount
          ? p.t('Can\'t delete "{{name}}"', { name: p.confirmTarget?.categoryName, defaultValue: `Can't delete "${p.confirmTarget?.categoryName}"` })
          : p.t('Delete "{{name}}"?', { name: p.confirmTarget?.categoryName, defaultValue: `Delete "${p.confirmTarget?.categoryName}"?` })}
        description={p.confirmTarget?.productCount
          ? p.t('{{count}} product(s) still use this category. Move them to another category first, then delete it.', {
            count: p.confirmTarget.productCount,
            defaultValue: `${p.confirmTarget.productCount} product(s) still use this category. Move them to another category first, then delete it.`,
          })
          : p.t('This cannot be undone.', { defaultValue: 'This cannot be undone.' })}
        cancelLabel={p.t('Cancel')}
        confirmLabel={p.t('Delete', { defaultValue: 'Delete' })}
        onConfirm={p.handleConfirmDelete}
        confirmDisabled={Boolean(p.confirmTarget?.productCount)}
        loading={p.deleting}
      />

      <Snackbar
        open={p.snackbar.open}
        autoHideDuration={4000}
        onClose={() => p.setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={p.snackbar.severity} sx={{ width: '100%' }}>
          {p.snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Categories;
